// src/pages/Dashboard/Dashboard.jsx
import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDashboard } from '../../hooks/useDashboard';
import UploadBox from '../../components/UploadBox/UploadBox';
import DiagramViewer from '../../components/DiagramViewer/DiagramViewer';
import ComponentList from '../../components/ComponentList/ComponentList';
import Toolbar from '../../components/Toolbar/Toolbar';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import './Dashboard.css';

export default function Dashboard() {
  const {
    image, handleUpload,
    components, filteredComponents, loading,
    selectedId, handleSelectComponent,
    zoom, setZoom, pan, setPan, handleZoomIn, handleZoomOut, handleReset,
    sidebarOpen, setSidebarOpen,
    diagramContrast, setDiagramContrast,
    searchQuery, setSearchQuery,
    activeCategory, setActiveCategory,
    fileHistory,
    autoZoom, setAutoZoom,
  } = useDashboard();

  return (
    <div className="dashboard">
      <header className="dash-header glass">
        <div className="header-left">
          <div className="logo">
            <span className="logo-icon">⬡</span>
            <div>
              <div className="logo-name">Circuit<span className="glow-text">IQ</span></div>
              <div className="logo-sub">Intelligence Dashboard</div>
            </div>
          </div>
          <Breadcrumb image={image} fileHistory={fileHistory} />
        </div>
        <div className="header-right">
          <UploadBox image={image} onUpload={handleUpload} />
        </div>
      </header>

      <main className="dash-main">
        <div className="toolbar-float">
          <Toolbar
            zoom={zoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleReset}
            diagramContrast={diagramContrast}
            setDiagramContrast={setDiagramContrast}
            autoZoom={autoZoom}
            setAutoZoom={setAutoZoom}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            disabled={!image}
          />
        </div>

        <div className="viewer-wrap">
          <DiagramViewer
            image={image}
            components={components}
            selectedId={selectedId}
            zoom={zoom}
            setZoom={setZoom}
            pan={pan}
            setPan={setPan}
            diagramContrast={diagramContrast}
            onSelectComponent={handleSelectComponent}
            autoZoom={autoZoom}
          />
        </div>

        <AnimatePresence>
          {sidebarOpen && (
            <motion.aside
              className="sidebar"
              initial={{ opacity: 0, x: 40, width: 0 }}
              animate={{ opacity: 1, x: 0, width: 300 }}
              exit={{ opacity: 0, x: 40, width: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 28 }}
            >
              <ComponentList
                components={filteredComponents}
                loading={loading}
                selectedId={selectedId}
                onSelect={handleSelectComponent}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
              />
            </motion.aside>
          )}
        </AnimatePresence>
      </main>

      <footer className="dash-footer">
        <span className="status-dot" style={{ background: image ? 'var(--success)' : 'var(--text-muted)' }} />
        <span>{image ? components.length + ' components detected' : 'No diagram loaded'}</span>
        {selectedId && (
          <>
            <span className="footer-sep">·</span>
            <span className="glow-text">
              {components.find((c) => c.id === selectedId)?.name} selected
            </span>
          </>
        )}
        <span className="footer-spacer" />
        <span className="footer-hint">Scroll to zoom · Drag to pan · Click bbox to select</span>
      </footer>
    </div>
  );
}
